// TODO we probably don't need these React imports here
import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'
import ReactModal from 'react-modal'
import { createRoot } from 'react-dom/client'
import {
  SHARED_COMPONENTS_EN_US,
  SHARED_COMPONENTS_ES_ES,
  SHARED_COMPONENTS_FR_FR,
} from '@retailer-platform/shared-components/src/exports/shared-components-core'
import {
  SHARED_ENTERPRISE_TOOLS_EN_US,
  SHARED_ENTERPRISE_TOOLS_ES_ES,
  SHARED_ENTERPRISE_TOOLS_FR_FR,
} from '@retailer-platform/domain-shared-enterprise-tools/shared-enterprise-tools-core'
import { pollyServerManager } from '@app/polly-loader'
// eslint-disable-next-line @retailer-platform/no-restricted-imports
import { instacart } from '@retailer-platform/dashboard/legacy-core'
import { initSentry } from '@retailer-platform/dashboard/errors'
import { initAnalytics, initDataDogRum } from '@retailer-platform/dashboard/analytics'
import { markBootStart, trackStaticAssetLoadTime } from '@retailer-platform/dashboard/performance'
import { RPPCore } from '@retailer-platform/dashboard/core'
import { clientEnv } from '@retailer-platform/dashboard/env'

// Initialize pollyServerManager if available (CI builds only)
if (pollyServerManager) {
  pollyServerManager.getInstanceId()
}

/**
 * Initialize analytics services
 *
 * Strategy:
 * - Sentry: Always initialize (error tracking is essential)
 * - Analytics & Datadog: Handled by PrivacyComplianceProvider based on user region
 *   - If OneTrust configured: PrivacyComplianceProvider detects region via IP
 *     - EU users: Load OneTrust and wait for consent
 *     - Non-EU users: Auto-enable analytics (no OneTrust needed)
 *   - If OneTrust NOT configured: Initialize immediately (backward compatible)
 */
initSentry() // Always initialize error tracking

// Start timing for framework boot measurement
markBootStart()
trackStaticAssetLoadTime()

// Check if OneTrust is configured (GDPR consent management)
const hasOneTrustConfigured = !!clientEnv.PUBLIC_CLIENT_ONETRUST_DOMAIN_UUID

if (hasOneTrustConfigured) {
  // OneTrust configured: PrivacyComplianceProvider will handle region detection and analytics initialization
  console.log(
    '[Analytics Init] OneTrust configured - PrivacyComplianceProvider will handle region-based initialization'
  )

  // Note: initAnalytics() and initDataDogRum() will be called by:
  // 1. PrivacyComplianceProvider.tsx - detects user region via IP
  //    - EU users: Loads OneTrust and waits for consent
  //    - Non-EU users: Auto-enables analytics (implied consent)
  // 2. PublicLogIn.tsx (for public/login pages) - listens for OneTrust consent changes
} else {
  // No OneTrust: Initialize analytics immediately (backward compatible)
  console.log('[Analytics Init] No OneTrust configured - initializing analytics immediately')
  initAnalytics()
  initDataDogRum()
}

/* Vendor Setup */
ReactModal.setAppElement('#root')

instacart.init({ mode: (process.env.NODE_ENV || 'development') as NodeEnv })
/**
 * We create an instance of the application,
 * this will allow us to register domains
 */
const app = new RPPCore({})

app.addTranslations({
  'en-US': {
    ...SHARED_COMPONENTS_EN_US,
    ...SHARED_ENTERPRISE_TOOLS_EN_US,
  },
  'es-ES': {
    ...SHARED_COMPONENTS_ES_ES,
    ...SHARED_ENTERPRISE_TOOLS_ES_ES,
  },
  'fr-FR': {
    ...SHARED_COMPONENTS_FR_FR,
    ...SHARED_ENTERPRISE_TOOLS_FR_FR,
  },
})

/**
 * Actually start the application
 *
 * Uses lazy domain loading to run domain import in parallel with account fetch.
 * The domain registry is split into 4 chunks that download and parse in parallel,
 * reducing the JS parse bottleneck from a single large chunk.
 */
const startApp = () => {
  // Start all 4 domain registry chunks in parallel.
  // The browser can download and parse these concurrently on separate threads,
  // significantly reducing total parse time vs a single 2.2MB chunk.
  const domainPromise = Promise.all([
    import('./domainRegistryGroup1'),
    import('./domainRegistryGroup2'),
    import('./domainRegistryGroup3'),
    import('./domainRegistry'),
  ]).then(groups => ({
    registerDomains: (app: RPPCore) => {
      groups.forEach(g => g.registerDomains(app))
    },
  }))

  // Start rendering immediately - account fetch will begin in AppProviderPreloader
  // while domain import continues in the background
  app.start(domainPromise, tree => {
    const container = document.getElementById('root')
    const root = createRoot(container)
    root.render(tree)
  })
}

startApp()
